import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText } from "@/components";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { JobIntake, JobIntakeScreeningQuestions } from "@/types";

const screeningQuestionsSchema = z.object({
  dealBreakers: z.string().nullable(),
  contextBuilders: z.string().nullable(),
});

export const JobScreeningQuestions = ({
  isEditing,
  jobIntake,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
}) => {
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  const handleSave = (data: JobIntakeScreeningQuestions) => {
    updateJobIntake({ updateJobIntakeRequest: { screeningQuestions: data } });
  };

  return (
    <Column className="gap-4">
      {isEditing ? (
        <ScreeningQuestionsForm
          screeningQuestions={jobIntake.screeningQuestions}
          onSave={handleSave}
        />
      ) : (
        <ScreeningQuestionsReadOnly
          screeningQuestions={jobIntake.screeningQuestions}
        />
      )}
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const ScreeningQuestionsForm = ({
  screeningQuestions,
  onSave,
}: {
  screeningQuestions: JobIntakeScreeningQuestions;
  onSave: (data: JobIntakeScreeningQuestions) => void;
}) => {
  const form = useForm<z.infer<typeof screeningQuestionsSchema>>({
    resolver: zodResolver(screeningQuestionsSchema),
    defaultValues: screeningQuestions,
  });

  const saveOnBlur = () => {
    void form.handleSubmit(onSave)();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-8">
        <AutosaveFormRichTextInput
          fieldName="dealBreakers"
          form={form}
          infoTooltip={`What questions does the interviewer ask that weed out unqualified candidates?\nWhy do they ask those — what are they really trying to find out?\nWhat answers would immediately disqualify someone?`}
          label="Deal Breakers"
          editorHeight="200px"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          fieldName="contextBuilders"
          form={form}
          infoTooltip={`What questions does the interviewer ask to dig into a candidate's experience and fit?\nWhy are they asking those — what insight are they looking for?\nWhat does a strong answer look like so we can prep candidates ahead of time?`}
          label="Context Builders"
          editorHeight="200px"
          onBlur={saveOnBlur}
        />
      </Column>
    </IntakeForm>
  );
};

const ScreeningQuestionsReadOnly = ({
  screeningQuestions,
}: {
  screeningQuestions: JobIntakeScreeningQuestions;
}) => {
  return (
    <Column className="gap-6">
      <ReadOnlyRichTextField
        label="Deal Breakers"
        value={screeningQuestions.dealBreakers}
      />
      <ReadOnlyRichTextField
        label="Context Builders"
        value={screeningQuestions.contextBuilders}
      />
    </Column>
  );
};
